import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";

import debounce from "../../../../utils/debounce";
import { RESULTS_PER_PAGE } from "../../../../utils/constants";
import { useURLQueryString } from "../../../../hooks/useUrlQueryString";

import { SpinnerLoader } from "../../../../components/loaders";
import { Pagination } from "../../../../components/Pagination/pagination.component";
import { getTotalPages } from "../../../../components/Pagination/pagination.functions";

import * as store from "../../store/flyer.store";

import { flyerStyles } from "./flyer.styles";
import { FlyerListTable } from "../flyer-list-table/flyer-list-table.component";
import { IFlyerFilters } from "../../types/flyer-filter.interface";
import { sortFlyersByCreatedDate } from "../../types/flyer-detail-data/flyer-detail-data.functions";
import * as userStore from "../../../../features/user/store/user.store";
import { FilterChips } from "../../../../components/filters/components/filter-chips/filter-chips.components";
import { BasicResourceType } from "../../../../types/resources/resources.enums";
import * as FlyerStore from "../../store/flyer.store";

export const Flyer = () => {
  const classes = flyerStyles();

  const {
    values: { limit = [RESULTS_PER_PAGE], page = [1], ...parameters },
  } = useURLQueryString();

  const limitRef = useRef(limit);
  const pageRef = useRef(page);
  const paramsRef = useRef(parameters ?? {});
  const isLoading = useSelector(store.isLoading);
  const flyerList = useSelector(store.getFlyers);
  const permissions = useSelector(store.getPermissions);
  const user = useSelector(userStore.getUser);
  const sortedFlyersList = sortFlyersByCreatedDate(flyerList?.results ?? []);
  const showList = () => !!sortedFlyersList.length && !isLoading;
  const totalResults = flyerList?.count ?? 0;
  const fetchFlyers = (params: Partial<IFlyerFilters>) => store.fetchFlyers(params);
  const flyerOwners = useSelector(FlyerStore.getFlyerOwners) ?? [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((params) => fetchFlyers(params), 1000),
    []
  );

  useEffect(() => {
    if (!_.isEqual(paramsRef.current, parameters)) {
      const newParams = { limit: limitRef.current, page: pageRef.current, ...parameters };

      if (_.isEqual(paramsRef.current, {}) || _.isEqual(parameters, {})) {
        fetchFlyers(newParams);
      } else {
        debouncedFetch(newParams);
      }

      paramsRef.current = parameters;
    } else if (!_.isEqual([limitRef.current, pageRef.current], [limit, page])) {
      limitRef.current = limit;
      pageRef.current = page;
      fetchFlyers({ limit, page, ...paramsRef.current });
    }
  }, [debouncedFetch, limit, page, parameters]);

  useEffect(() => {
    store.fetchFilterOptions();
    fetchFlyers({ limit, page, ...parameters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (permissions === null && Boolean(user.id)) {
      store.initPermissions(user);
    }
  }, [permissions, user]);

  return (
    <div className={classes.container}>
      <FilterChips filterType={BasicResourceType.Flyer} optionLists={{ users: flyerOwners }} />
      <SpinnerLoader showIf={isLoading} size={40} />
      <FlyerListTable dataList={sortedFlyersList} showIf={showList()} />
      <Pagination count={getTotalPages(totalResults)} />
    </div>
  );
};
